import { getUiEventTarget } from '../shadowDom';
import { listenInOwnerDocument } from '../shadowDom';
import { mountFeatureDot } from './featureGuideController';
import { CAPTURE_MODE_COPY } from '../constants/captureCopy';
import { FOUNDATIONS } from '../designFoundations';
import { DESIGN_RECIPES } from '../designRecipes';
import { typographyCss } from './typographyCss';
import { PALETTE, paletteRgba } from '../designPalette';
import { mountSpacingInspectorButton } from '../spacing/inspectorButton';
import { CONTROL_PROFILES } from '../controlProfiles';
import { PauseIcon, PlayIcon, Tick02Icon, DragDropVerticalIcon, CursorInfo01Icon, Cursor01Icon, Camera01Icon, Video01Icon, StopIcon, ArrowLeft01Icon, ArrowRight01Icon, ArrowUp01Icon, EyeIcon, ViewOffIcon, Location01Icon, BorderAll01Icon, FlashIcon, SquareLock02Icon } from '@hugeicons/core-free-icons';
import { createIcon } from './iconDom';
import { createToggle, updateToggle } from './toggleDom';
import { mountToolbarTooltips, toolbarTooltipCss } from './toolbarTooltipDom';
import { WORKFLOW_GLYPH_ICONS } from './workflowGlyphData';
const LAYER_IDS = ['pins', 'boxes'];
const TOOL_KINDS = ['select', 'screenshot'];
/**
 * The exit curve for Pause and Done when capture ends: `--oq-ease-in-m3`,
 * read off computed style at morph time because `element.animate()` rejects
 * `var()` in its easing string. This is that token's own value.
 */
const EXIT_EASING_FALLBACK = 'cubic-bezier(.3,0,.8,.15)';
// Resolve the app profile's rem units to fixed embedded pixels, as the controls
// generator does for :host. A customer's root font must not resize our toolbar.
const controlHeight = `${parseFloat(CONTROL_PROFILES.app.standard.height) * 16}px`;
const smallHeight = `${parseFloat(CONTROL_PROFILES.app.small.height) * 16}px`;
const edgeInset = parseFloat(DESIGN_RECIPES.spacing.related);
// One renderer for the extension, authenticated review and standalone guests.
// ds-hand-rolled: viewport z-index, inherited 236px menu width and glyph geometry
// (checkbox tick, grip/caret slots) retain their shipped structural/optical jobs.
const css = `
.oq-capture-toolbar.oq-canvas__toolbar{box-sizing:border-box;display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:${FOUNDATIONS.space[3]};width:max-content;max-width:calc(100% - ${FOUNDATIONS.space[4]});padding:${FOUNDATIONS.space[3]} ${FOUNDATIONS.space[4]};bottom:${FOUNDATIONS.space[12]};left:50%;transform:translateX(-50%);border:0;border-radius:${FOUNDATIONS.radius.panel};background:${PALETTE.surface.dark};color:${PALETTE.surface.card};${typographyCss('caption')};font-family:${FOUNDATIONS.fontFamily.system};box-shadow:${FOUNDATIONS.shadow.popover};pointer-events:auto;z-index:2147483647}
.oq-capture-toolbar .oq-canvas__toolbar-text--muted{display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:${FOUNDATIONS.space[1]};color:${PALETTE.neutral[400]}}
.oq-capture-toolbar .oq-canvas__toolbar-kbd{box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;height:${smallHeight};padding:0 ${FOUNDATIONS.space[2]};${typographyCss('caption')};color:${PALETTE.surface.card};background:${paletteRgba(PALETTE.surface.card, .15)};border:${DESIGN_RECIPES.border.thin} solid ${paletteRgba(PALETTE.surface.card, .25)};border-radius:${FOUNDATIONS.radius.sm}}
/* The Record states hide Pause, so their hint sits straight beside the primary button; this margin plus the compact bar's gap is the full bar's control spacing (Emily, 2026-10-25). */
.oq-capture-toolbar .oq-canvas__toolbar-text--muted[data-state="record"]{margin-inline-end:${FOUNDATIONS.space[2]}}
/* Dark colors match _canvas.css; sizing uses the shared toolbar profile. */
.oq-capture-toolbar button:where(:not(.oq-toggle)){box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;gap:${FOUNDATIONS.space[2]};flex-shrink:0;height:${controlHeight};margin:0;padding:0 ${FOUNDATIONS.space[3]};border:0;border-radius:${FOUNDATIONS.radius.control};${typographyCss('label')};font-weight:${FOUNDATIONS.fontWeight.semibold};color:${PALETTE.surface.card};cursor:pointer;white-space:nowrap;text-transform:none;transition:background-color var(--oq-motion-feedback, .15s),border-color var(--oq-motion-feedback, .15s),color var(--oq-motion-feedback, .15s),transform var(--oq-motion-feedback, 140ms) var(--oq-ease-out,cubic-bezier(.16,1,.3,1))}
.oq-capture-toolbar button svg{display:block;flex-shrink:0;width:${DESIGN_RECIPES.icon.standard};height:${DESIGN_RECIPES.icon.standard}}
.oq-capture-toolbar .oq-canvas__done-btn{padding-inline:${FOUNDATIONS.space[4]};background:${PALETTE.brand.primary}}
.oq-capture-toolbar .oq-canvas__pause-btn,.oq-capture-toolbar .oq-capture-toolbar__spacing,.oq-capture-toolbar .oq-capture-toolbar__highlights{width:${controlHeight};padding:0;background:var(--oq-dark-surface-ring,${paletteRgba(PALETTE.surface.card, .15)})}
.oq-capture-toolbar .oq-canvas__pause-btn:hover:not(:disabled),.oq-capture-toolbar .oq-capture-toolbar__spacing:hover:not(:disabled),.oq-capture-toolbar .oq-capture-toolbar__highlights:hover:not(:disabled),.oq-capture-toolbar .oq-capture-toolbar__grip:hover{background:var(--oq-dark-surface-border-soft,${paletteRgba(PALETTE.surface.card, .25)})}
.oq-capture-toolbar button[aria-pressed="true"]{background:${PALETTE.brand.primary}}
.oq-capture-toolbar button[aria-pressed="true"]:hover:not(:disabled),.oq-capture-toolbar .oq-canvas__done-btn:hover:not(:disabled){background:${PALETTE.brand.hover}}
/* The compact bar (tools present) reserves brand blue for Done: a pressed tool, and the paused Pause button, are neutral tiles. Same specificity as the two rules above, so these win by order. */
.oq-capture-toolbar--tools.oq-canvas__toolbar{gap:${FOUNDATIONS.space[1]};padding:${FOUNDATIONS.space[2]}}
.oq-capture-toolbar .oq-capture-toolbar__tool{width:${controlHeight};padding:0;background:transparent;color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar .oq-capture-toolbar__collapse{width:${FOUNDATIONS.space[6]};padding:0;background:transparent;color:${PALETTE.neutral[400]}}
.oq-capture-toolbar--tools .oq-canvas__pause-btn,.oq-capture-toolbar--tools .oq-capture-toolbar__spacing,.oq-capture-toolbar--tools .oq-capture-toolbar__highlights{background:transparent;color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar .oq-capture-toolbar__tool:hover:not(:disabled),.oq-capture-toolbar .oq-capture-toolbar__collapse:hover:not(:disabled),.oq-capture-toolbar--tools .oq-canvas__pause-btn:hover:not(:disabled),.oq-capture-toolbar--tools .oq-capture-toolbar__spacing:hover:not(:disabled),.oq-capture-toolbar--tools .oq-capture-toolbar__highlights:hover:not(:disabled){background:${paletteRgba(PALETTE.surface.card, .10)};color:${PALETTE.surface.card}}
.oq-capture-toolbar--tools button[aria-pressed="true"],.oq-capture-toolbar--tools button[aria-pressed="true"]:hover:not(:disabled){background:${paletteRgba(PALETTE.surface.card, .16)};box-shadow:inset 0 0 0 1px ${paletteRgba(PALETTE.surface.card, .28)};color:${PALETTE.surface.card}}
.oq-capture-toolbar .oq-capture-toolbar__divider{width:${DESIGN_RECIPES.border.thin};height:${FOUNDATIONS.space[5]};margin:0 ${FOUNDATIONS.space[1]};flex-shrink:0;background:${paletteRgba(PALETTE.surface.card, .18)}}
.oq-capture-toolbar .oq-capture-toolbar__count{display:inline-flex;align-items:center;justify-content:center;min-width:${FOUNDATIONS.space[5]};height:${FOUNDATIONS.space[5]};padding:0 ${FOUNDATIONS.space[2]};margin-left:${FOUNDATIONS.space[2]};border-radius:${DESIGN_RECIPES.corner.pill};background:${paletteRgba(PALETTE.surface.card, .22)};${typographyCss('micro')};font-weight:${FOUNDATIONS.fontWeight.semibold}}
.oq-capture-toolbar--collapsed .oq-capture-toolbar__tool,.oq-capture-toolbar--collapsed .oq-capture-toolbar__workflows,.oq-capture-toolbar--collapsed .oq-capture-toolbar__divider,.oq-capture-toolbar--collapsed .oq-capture-toolbar__spacing-host,.oq-capture-toolbar--collapsed .oq-capture-toolbar__highlights,.oq-capture-toolbar--collapsed .oq-canvas__toolbar-text--muted,.oq-capture-toolbar--collapsed .oq-canvas__pause-btn{display:none!important}
.oq-capture-toolbar button:where(:not(.oq-toggle)):focus{outline:none}
.oq-capture-toolbar button:where(:not(.oq-toggle)):focus-visible{outline:${DESIGN_RECIPES.border.focus} solid ${PALETTE.brand.primary};outline-offset:${DESIGN_RECIPES.border.thin};box-shadow:none}
.oq-capture-toolbar button:where(:not(.oq-toggle)):active:not(:disabled){transform:scale(.97)}
@media(prefers-reduced-motion:reduce){.oq-capture-toolbar button:where(:not(.oq-toggle)):active:not(:disabled){transform:none}}
.oq-capture-toolbar button:disabled{opacity:.5;cursor:not-allowed}
.oq-capture-toolbar .oq-capture-toolbar__grip{width:${FOUNDATIONS.space[5]};padding:0;background:transparent;color:${PALETTE.neutral[400]};cursor:grab;touch-action:none;user-select:none}
.oq-capture-toolbar .oq-capture-toolbar__grip:active{cursor:grabbing}
/* The highlights menu (variant B, Emily 2026-10-19). The eye reads mixed with a brand dot when one layer is
   off; the caret is drawn like the collapse chevron; the sheet sits on the toolbar's own surface and ink
   (prototype docs/prototypes/highlight-visibility-menu/menu.css), 8px above the bar, rising out of it on the
   pin popover's entrance timing (_popover.css: 260ms, --oq-ease-out-m3). Rows carry no stroke; the one
   hairline is the divider before Hide all. Rows reuse the shared Toggle, with its own canonical stylesheet. */
.oq-capture-toolbar .oq-capture-toolbar__highlights{position:relative}
.oq-capture-toolbar .oq-capture-toolbar__highlights--mixed::after{content:"";position:absolute;top:${FOUNDATIONS.space[2]};right:${FOUNDATIONS.space[2]};width:${FOUNDATIONS.space[2]};height:${FOUNDATIONS.space[2]};border-radius:50%;background:${PALETTE.brand.primary};box-shadow:0 0 0 2px ${PALETTE.surface.dark}}
.oq-capture-toolbar--collapsed .oq-capture-toolbar__select-group,.oq-capture-toolbar--collapsed .oq-capture-toolbar__highlights-group{display:none!important}
.oq-capture-toolbar__menu{position:absolute;bottom:calc(100% + ${FOUNDATIONS.space[2]});left:0;box-sizing:border-box;min-width:236px;padding:${FOUNDATIONS.space[1]} 0;background:${PALETTE.surface.dark};border:${DESIGN_RECIPES.border.thin} solid ${paletteRgba(PALETTE.surface.card, .25)};border-radius:${FOUNDATIONS.radius.control};box-shadow:${FOUNDATIONS.shadow.popover};color:${PALETTE.surface.card};${typographyCss('body')};font-family:${FOUNDATIONS.fontFamily.system};text-align:left;transform-origin:bottom center;animation:oq-capture-toolbar-menu-in 260ms var(--oq-ease-out-m3,cubic-bezier(.05,.7,.1,1))}
@keyframes oq-capture-toolbar-menu-in{from{opacity:0;transform:scale(.98) translateY(6px)}to{opacity:1;transform:scale(1) translateY(0)}}
@media (prefers-reduced-motion: reduce){.oq-capture-toolbar__menu{animation:none}}
.oq-capture-toolbar__menu-row{display:flex;align-items:center;gap:${FOUNDATIONS.space[2]};width:100%;box-sizing:border-box;margin:0;padding:${FOUNDATIONS.space[2]} ${FOUNDATIONS.space[3]};border:0;background:transparent;color:${PALETTE.surface.card};font:inherit;cursor:pointer}
.oq-capture-toolbar__menu-row:hover,.oq-capture-toolbar .oq-capture-toolbar__menu-all:hover:not(:disabled){background:${paletteRgba(PALETTE.surface.card, .08)}}
.oq-capture-toolbar__menu-row svg,.oq-capture-toolbar .oq-capture-toolbar__menu-all svg{flex-shrink:0;color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar__menu-label{flex:1 1 auto}
.oq-capture-toolbar__menu-count{color:${paletteRgba(PALETTE.surface.card, .7)};font-variant-numeric:tabular-nums;${typographyCss('caption')}}
.oq-capture-toolbar__menu-divider{height:${DESIGN_RECIPES.border.thin};margin:${FOUNDATIONS.space[1]} 0;background:${paletteRgba(PALETTE.surface.card, .1)}}
.oq-capture-toolbar .oq-capture-toolbar__menu-all{display:flex;align-items:center;justify-content:flex-start;gap:${FOUNDATIONS.space[2]};width:100%;height:auto;box-sizing:border-box;padding:${FOUNDATIONS.space[2]} ${FOUNDATIONS.space[3]};border:0;border-radius:0;background:transparent;color:${PALETTE.surface.card};${typographyCss('body')};text-align:left;white-space:nowrap}
.oq-capture-toolbar .oq-capture-toolbar__menu-all .oq-canvas__toolbar-kbd{margin-left:auto}
/* The Workflows menu button (2026-10-25): one control drawn like a tool, the bolt the Workflows tab
   wears plus the highlights caret, and the same sheet rising above it. Rows mirror the tab's cards:
   glyph on the first line, the label, the card's one-line description beneath in the menu-count ink. */
/* One menu-trigger treatment. Highlights keeps two actions inside the same silhouette. */
.oq-capture-toolbar .oq-capture-toolbar__menu-trigger{display:inline-flex;align-items:center;flex-shrink:0;height:${controlHeight};gap:${FOUNDATIONS.space[1]};padding:0 ${FOUNDATIONS.space[2]};border-radius:${FOUNDATIONS.radius.control};background:transparent;color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar .oq-capture-toolbar__menu-trigger svg[data-toolbar-icon$="-caret"]{width:${DESIGN_RECIPES.icon.compact};height:${DESIGN_RECIPES.icon.compact};color:${PALETTE.neutral[400]}}
.oq-capture-toolbar .oq-capture-toolbar__menu-trigger:hover:not(:disabled),.oq-capture-toolbar .oq-capture-toolbar__menu-trigger[aria-expanded="true"],.oq-capture-toolbar .oq-capture-toolbar__menu-trigger:has([aria-expanded="true"]){background:${paletteRgba(PALETTE.surface.card, .10)};color:${PALETTE.surface.card}}
.oq-capture-toolbar .oq-capture-toolbar__highlights-group{gap:0;padding:0}
.oq-capture-toolbar .oq-capture-toolbar__highlights-group>button,.oq-capture-toolbar .oq-capture-toolbar__highlights-group>button:hover:not(:disabled){width:auto;background:transparent;box-shadow:none;color:inherit}
.oq-capture-toolbar .oq-capture-toolbar__highlights-group>.oq-capture-toolbar__highlights{padding:0 ${FOUNDATIONS.space[1]} 0 ${FOUNDATIONS.space[2]}}
.oq-capture-toolbar .oq-capture-toolbar__highlights-group>.oq-capture-toolbar__highlights-caret{padding:0 ${FOUNDATIONS.space[2]} 0 0}
.oq-capture-toolbar .oq-capture-toolbar__menu-trigger.oq-capture-toolbar__select-group { gap: 0; padding: 0; }
.oq-capture-toolbar .oq-capture-toolbar__select-group:has([aria-pressed="true"]) { background: rgb(var(--oq-palette-surface-card-rgb) / 0.16); outline: var(--oq-border-thin) solid rgb(var(--oq-palette-surface-card-rgb) / 0.28); outline-offset: calc(-1 * var(--oq-border-thin)); color: var(--oq-palette-surface-card); }
.oq-capture-toolbar .oq-capture-toolbar__select-group button:not(.oq-feature-dot),
.oq-capture-toolbar .oq-capture-toolbar__select-group button:not(.oq-feature-dot):hover:not(:disabled) { width: auto; background: transparent; box-shadow: none; color: inherit; }
.oq-capture-toolbar .oq-capture-toolbar__select-group > .oq-capture-toolbar__tool { padding: 0 var(--oq-space-1) 0 var(--oq-space-2); }
.oq-capture-toolbar .oq-capture-toolbar__select-group .oq-capture-toolbar__select-caret { padding: 0 var(--oq-space-2) 0 0; }
.oq-capture-toolbar__menu--workflows{width:max-content}
.oq-capture-toolbar .oq-capture-toolbar__menu-item{display:flex;align-items:flex-start;justify-content:flex-start;gap:${FOUNDATIONS.space[2]};width:100%;height:auto;box-sizing:border-box;padding:${FOUNDATIONS.space[2]} ${FOUNDATIONS.space[3]};border:0;border-radius:0;background:transparent;color:${PALETTE.surface.card};${typographyCss('body')};text-align:left;white-space:nowrap}
.oq-capture-toolbar .oq-capture-toolbar__menu-item:hover:not(:disabled){background:${paletteRgba(PALETTE.surface.card, .08)}}
.oq-capture-toolbar .oq-capture-toolbar__menu-item:focus-visible{outline:none;box-shadow:inset 0 0 0 2px ${paletteRgba(PALETTE.brand.primary, .5)}}
.oq-capture-toolbar .oq-capture-toolbar__menu-item svg{margin-top:calc((${DESIGN_RECIPES.text.body.lineHeight} - ${DESIGN_RECIPES.icon.standard}) / 2);color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar__menu-item-text{display:flex;flex-direction:column}
.oq-capture-toolbar__menu-item-label{font-weight:${FOUNDATIONS.fontWeight.medium}}
.oq-capture-toolbar__menu-item-desc{${typographyCss('caption')};color:${paletteRgba(PALETTE.surface.card, .7)}}
/* Locked tools (free-tool sessions, 2026-10-26): a padlock in the tile's lower corner, the upper corner being the
   feature dot's and the mixed eye's. A locked workflow row keeps its glyph and description, mutes its label and
   trails the padlock the light menus' .oq-dropdown__item--locked uses. */
.oq-capture-toolbar .oq-capture-toolbar__locked{position:relative}
.oq-capture-toolbar .oq-capture-toolbar__lock{position:absolute;right:${DESIGN_RECIPES.border.focus};bottom:${DESIGN_RECIPES.border.focus};display:grid;place-items:center;width:${FOUNDATIONS.space[3]};height:${FOUNDATIONS.space[3]};border-radius:${DESIGN_RECIPES.corner.pill};background:${PALETTE.surface.dark};color:${PALETTE.surface.darkMuted};pointer-events:none}
.oq-capture-toolbar button .oq-capture-toolbar__lock svg{width:${FOUNDATIONS.space['2h']};height:${FOUNDATIONS.space['2h']}}
.oq-capture-toolbar .oq-capture-toolbar__locked:hover .oq-capture-toolbar__lock,.oq-capture-toolbar .oq-capture-toolbar__locked:focus-visible .oq-capture-toolbar__lock{color:${PALETTE.surface.card}}
.oq-capture-toolbar .oq-capture-toolbar__workflows .oq-capture-toolbar__lock{right:auto;left:calc(${FOUNDATIONS.space[2]} + ${FOUNDATIONS.space['2h']})}
.oq-capture-toolbar .oq-capture-toolbar__menu-item--locked .oq-capture-toolbar__menu-item-label{color:${PALETTE.surface.darkMuted}}
.oq-capture-toolbar .oq-capture-toolbar__menu-item--locked svg[data-toolbar-icon="lock"]{width:${DESIGN_RECIPES.icon.compact};height:${DESIGN_RECIPES.icon.compact};margin-inline-start:auto;padding-inline-start:${FOUNDATIONS.space[4]};box-sizing:content-box}
${toolbarTooltipCss}
.oq-capture-tooltip.oq-capture-tooltip--app14{${typographyCss('label')};font-family:${FOUNDATIONS.fontFamily.system};box-shadow:${FOUNDATIONS.shadow.popover}}
`;
/** Mount the actual toolbar; update keeps the controls, focus and drag position. */
export function mountCaptureToolbar(container, initial) {
    const doc = container.ownerDocument;
    const win = doc.defaultView;
    let options = initial;
    const track = (event, properties = {}) => {
        try {
            options.onTrack?.(event, { placement: 'capture_toolbar', ...properties });
        }
        catch { /* Tracking cannot block controls. */ }
    };
    // Also upgrade an already-served review shell when only the client bundle
    // is refreshed. Both review modes use this existing portal layer.
    const reviewLayer = initial.position === 'absolute' ? container.closest('#review-toolbar-mount') : null;
    const layoutOwner = container.closest('#review-left-panel');
    if (reviewLayer) {
        // review-page/shell.ts fits a full-width iframe with a transform. Its
        // overflow:hidden canvas can still scroll when a page field keeps focus
        // during resize. Clip that outer canvas; scrolling belongs to the iframe,
        // and the unscaled capture controls must stay in the visible panel.
        // Explicit minimums preserve the flex shrink that overflow:hidden supplied.
        if (layoutOwner)
            Object.assign(layoutOwner.style, { overflow: 'clip', minWidth: '0', minHeight: '0' });
        Object.assign(reviewLayer.style, { position: 'absolute', inset: '0', width: 'auto', maxWidth: 'none', transform: 'none' });
        const onboarding = reviewLayer.parentElement?.querySelector('#review-onboarding-mount');
        if (onboarding)
            onboarding.style.bottom = `calc(${FOUNDATIONS.space[12]} + var(--oq-capture-toolbar-height, calc(${controlHeight} + ${FOUNDATIONS.space[4]})) + ${FOUNDATIONS.space[4]})`;
    }
    const style = doc.createElement('style');
    style.textContent = css;
    const toolbar = doc.createElement('div');
    toolbar.className = 'oq-capture-toolbar oq-canvas__toolbar oq-canvas__toolbar--dark';
    toolbar.setAttribute('data-toolbar', '');
    toolbar.setAttribute('role', 'group');
    toolbar.setAttribute('aria-label', 'Capture controls');
    const grip = doc.createElement('button');
    grip.type = 'button';
    grip.className = 'oq-capture-toolbar__grip';
    grip.setAttribute('aria-label', 'Move capture toolbar');
    grip.dataset.tooltip = 'Move toolbar · Arrow keys to move · Home to reset';
    grip.append(createIcon(doc, DragDropVerticalIcon, 'move'));
    const hint = doc.createElement('span');
    hint.className = 'oq-canvas__toolbar-text--muted';
    const pause = doc.createElement('button');
    pause.type = 'button';
    const done = doc.createElement('button');
    done.type = 'button';
    done.className = 'oq-canvas__done-btn';
    done.append(createIcon(doc, Tick02Icon, 'done'));
    const doneLabel = doc.createElement('span');
    done.append(doneLabel);
    const countPill = doc.createElement('span');
    countPill.className = 'oq-capture-toolbar__count';
    countPill.setAttribute('aria-hidden', 'true');
    const spacingHost = doc.createElement('span');
    spacingHost.className = 'oq-capture-toolbar__spacing-host';
    spacingHost.style.display = 'contents';
    let spacing;
    const disableSpacing = () => getUiEventTarget(doc).dispatchEvent(new CustomEvent('oq:spacing-disable'));
    // A locked Spacing never reaches the inspector: this capture-phase listener on its host runs before the button's
    // own click (which would dispatch oq:spacing-toggle) and reports the lock instead.
    spacingHost.addEventListener('click', event => {
        const button = event.target?.closest?.('.oq-capture-toolbar__spacing');
        if (!options.locks?.spacing || !button)
            return;
        event.preventDefault();
        event.stopPropagation();
        options.locks.onLocked('spacing', button);
    }, true);
    const tools = initial.tools && initial.tools.capture !== false ? TOOL_KINDS.map(tool => {
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'oq-capture-toolbar__tool';
        button.dataset.tool = tool;
        button.append(createIcon(doc, tool === 'select' ? Cursor01Icon : Camera01Icon, tool));
        button.addEventListener('click', () => {
            if (options.record)
                return;
            // Decision 7 (Emily, 2026-10-14): a tool click while paused resumes AND
            // switches. Resume first so the surface's own pause handler runs its
            // bookkeeping (the extension unlocks hover, the guest re-arms the
            // bridge) before the mode changes under it.
            if (options.isPaused)
                options.onTogglePause();
            (tool === 'select' ? options.tools?.onSelect : options.tools?.onScreenshot)?.();
        });
        return button;
    }) : [];
    // The Record tool (team review and client links with Element Capture). Unlike the two tools above it is
    // created on whichever options first offer it, not only at mount: team review learns whether this browser
    // can record after its bar is up (useCaptureSession starts recordSupported at false until the guest bundle
    // answers oq:record-state-request), and it steps aside (display none) when the option is withdrawn, so the
    // DOM stays byte-identical where it is never offered. Its running states (options.record) swap the shipped
    // hint, Pause and Done for the phase's own hint and primary button; both exist only while a state is set.
    let recordTool = null;
    let recordDot;
    function ensureRecordTool() {
        if (recordTool)
            return recordTool;
        recordTool = doc.createElement('button');
        recordTool.type = 'button';
        recordTool.className = 'oq-capture-toolbar__tool';
        recordTool.dataset.tool = 'record';
        recordTool.append(createIcon(doc, Video01Icon, 'record'));
        recordTool.addEventListener('click', () => {
            if (options.record)
                return;
            if (options.locks?.record) {
                options.locks.onLocked('record', recordTool);
                return;
            }
            if (options.isPaused)
                options.onTogglePause();
            options.tools?.record?.onRecord();
        });
        // After mount the bar is live: slot it right after Screenshot, where the mount-time append puts it.
        const lastTool = tools[tools.length - 1];
        if (lastTool?.isConnected)
            lastTool.after(recordTool);
        return recordTool;
    }
    if (initial.tools?.record)
        ensureRecordTool();
    const recordHint = doc.createElement('span');
    recordHint.className = 'oq-canvas__toolbar-text--muted';
    recordHint.dataset.state = 'record';
    recordHint.setAttribute('role', 'status');
    const recordPrimary = doc.createElement('button');
    recordPrimary.type = 'button';
    recordPrimary.className = 'oq-canvas__done-btn';
    recordPrimary.dataset.recordPrimary = '';
    recordPrimary.addEventListener('click', () => options.record?.primary?.onClick());
    // The eye is created at mount like the tools: a bar that gains or loses it
    // is re-mounted by its React key (SpacingInspectorLayer.tsx).
    const highlightsBtn = initial.highlights ? doc.createElement('button') : null;
    if (highlightsBtn) {
        highlightsBtn.type = 'button';
        highlightsBtn.className = 'oq-capture-toolbar__highlights';
        highlightsBtn.addEventListener('click', () => options.highlights?.onToggle());
    }
    // One sheet at a time: the highlights menu (variant B) or the workflows menu.
    // `menu` is whichever is open and `openKind` says which anchor owns it.
    const menuId = `oq-capture-toolbar-menu-${Math.random().toString(36).slice(2, 9)}`;
    const workflowsMenuId = `${menuId}-workflows`;
    const createMenuId = `${menuId}-create`;
    const createGroup = tools[0] ? doc.createElement('div') : null;
    const createCaret = tools[0] ? doc.createElement('button') : null;
    let inspectorDot;
    if (createGroup && createCaret) {
        createGroup.className = 'oq-capture-toolbar__select-group oq-capture-toolbar__menu-trigger';
        createCaret.type = 'button';
        createCaret.className = 'oq-capture-toolbar__select-caret';
        createCaret.setAttribute('aria-label', CAPTURE_MODE_COPY.createOptions);
        createCaret.dataset.tooltip = CAPTURE_MODE_COPY.createOptions;
        createCaret.setAttribute('aria-haspopup', 'true');
        createCaret.setAttribute('aria-expanded', 'false');
        createCaret.setAttribute('aria-controls', createMenuId);
        createCaret.append(createIcon(doc, ArrowUp01Icon, 'select-caret'));
        createCaret.addEventListener('click', () => { if (openKind === 'create')
            closeMenu(true, 'trigger');
        else
            openMenu('create'); });
    }
    let menu = null;
    let openKind = null;
    // Variant B: the caret beside the eye. It exists only when the mount names
    // layers; the eye alone stays byte-for-byte the eye.
    const caret = initial.highlights?.layers ? doc.createElement('button') : null;
    if (caret) {
        caret.type = 'button';
        caret.className = 'oq-capture-toolbar__highlights-caret';
        caret.setAttribute('aria-label', CAPTURE_MODE_COPY.highlightOptions);
        caret.dataset.tooltip = CAPTURE_MODE_COPY.highlightOptions;
        caret.setAttribute('aria-haspopup', 'true');
        caret.setAttribute('aria-expanded', 'false');
        caret.setAttribute('aria-controls', menuId);
        caret.append(createIcon(doc, ArrowUp01Icon, 'highlights-caret'));
        caret.addEventListener('click', () => { if (openKind === 'highlights')
            closeMenu(true, 'trigger');
        else
            openMenu('highlights'); });
    }
    const highlightsGroup = highlightsBtn && caret ? doc.createElement('div') : null;
    if (highlightsGroup) {
        // Styling only: the eye and caret carry their own labels, and the open menu owns the
        // "Highlights on page" group name, so the wrapper must not announce a second group by that name.
        highlightsGroup.className = 'oq-capture-toolbar__highlights-group oq-capture-toolbar__menu-trigger';
        highlightsGroup.append(highlightsBtn, caret);
    }
    // The Workflows menu button, created at mount like the tools and the eye.
    const workflowsBtn = initial.workflows ? doc.createElement('button') : null;
    if (workflowsBtn) {
        workflowsBtn.type = 'button';
        workflowsBtn.className = 'oq-capture-toolbar__workflows oq-capture-toolbar__menu-trigger';
        workflowsBtn.setAttribute('aria-label', CAPTURE_MODE_COPY.workflows);
        workflowsBtn.dataset.tooltip = CAPTURE_MODE_COPY.workflows;
        workflowsBtn.setAttribute('aria-haspopup', 'menu');
        workflowsBtn.setAttribute('aria-expanded', 'false');
        workflowsBtn.setAttribute('aria-controls', workflowsMenuId);
        workflowsBtn.append(createIcon(doc, FlashIcon, 'workflows'), createIcon(doc, ArrowUp01Icon, 'workflows-caret'));
        workflowsBtn.addEventListener('click', () => { if (openKind === 'workflows')
            closeMenu(true, 'trigger');
        else
            openMenu('workflows'); });
    }
    const anchorOf = (kind) => (kind === 'create' ? createCaret : kind === 'highlights' ? caret : kind === 'workflows' ? workflowsBtn : null);
    const layersOf = () => options.highlights?.layers;
    /** Escape closes back to the anchor; arrows, Home and End walk the sheet's controls, wrapping. */
    function addMenuKeys(sheet) {
        sheet.addEventListener('keydown', event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeMenu(true, 'escape');
                return;
            }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
                return;
            event.preventDefault();
            event.stopPropagation();
            const items = Array.from(sheet.querySelectorAll('input, button'));
            // The sheet's own root: in the extension that is the overlay's shadow
            // root, where document.activeElement is only the host (mini run
            // 2026-10-25: arrows stuck on the first row of both menus).
            const index = items.indexOf(sheet.getRootNode().activeElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
                : event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
            items[next]?.focus();
        });
    }
    function buildInspectorMenu() {
        const sheet = doc.createElement('div');
        sheet.className = 'oq-capture-toolbar__menu';
        sheet.id = createMenuId;
        sheet.dataset.menu = 'create';
        sheet.setAttribute('role', 'group');
        sheet.setAttribute('aria-label', CAPTURE_MODE_COPY.createOptions);
        const row = doc.createElement('label');
        row.className = 'oq-capture-toolbar__menu-row';
        const label = doc.createElement('span');
        label.className = 'oq-capture-toolbar__menu-label';
        label.textContent = CAPTURE_MODE_COPY.hoverInspector;
        const toggle = createToggle(doc, { checked: !!options.inspector?.enabled, label: CAPTURE_MODE_COPY.hoverInspector,
            onChange: enabled => {
                if (!options.inspector || enabled === options.inspector.enabled)
                    return;
                options.inspector.onChange(enabled);
                track('hover_inspector_toggled', { enabled, via: 'toggle' });
            },
        });
        row.append(createIcon(doc, CursorInfo01Icon, 'inspector'), label, toggle);
        sheet.append(row);
        addMenuKeys(sheet);
        return sheet;
    }
    function buildHighlightsMenu() {
        const sheet = doc.createElement('div');
        sheet.className = 'oq-capture-toolbar__menu';
        sheet.id = menuId;
        sheet.setAttribute('role', 'group');
        sheet.setAttribute('aria-label', CAPTURE_MODE_COPY.highlightsMenu);
        for (const id of LAYER_IDS) {
            const row = doc.createElement('label');
            row.className = 'oq-capture-toolbar__menu-row';
            row.dataset.layer = id;
            const toggle = createToggle(doc, { checked: !!layersOf()?.[id].visible,
                label: id === 'pins' ? CAPTURE_MODE_COPY.layerPins : CAPTURE_MODE_COPY.layerBoxes,
                onChange: visible => layersOf()?.[id].onToggle(visible),
            });
            const label = doc.createElement('span');
            label.className = 'oq-capture-toolbar__menu-label';
            label.textContent = id === 'pins' ? CAPTURE_MODE_COPY.layerPins : CAPTURE_MODE_COPY.layerBoxes;
            const count = doc.createElement('span');
            count.className = 'oq-capture-toolbar__menu-count';
            // Counts are supporting text; the switch retains a stable accessible name.
            row.append(createIcon(doc, id === 'pins' ? Location01Icon : BorderAll01Icon, id), label, doc.createTextNode(' '), count, toggle);
            sheet.append(row);
        }
        const divider = doc.createElement('div');
        divider.className = 'oq-capture-toolbar__menu-divider';
        const all = doc.createElement('button');
        all.type = 'button';
        all.className = 'oq-capture-toolbar__menu-all';
        all.addEventListener('click', () => (layersOf()?.onToggleAll ?? options.highlights?.onToggle)?.());
        sheet.append(divider, all);
        addMenuKeys(sheet);
        return sheet;
    }
    function buildWorkflowsMenu() {
        const sheet = doc.createElement('div');
        sheet.className = 'oq-capture-toolbar__menu oq-capture-toolbar__menu--workflows';
        sheet.id = workflowsMenuId;
        sheet.setAttribute('role', 'menu');
        sheet.setAttribute('aria-label', CAPTURE_MODE_COPY.workflows);
        for (const item of options.workflows?.items ?? []) {
            const row = doc.createElement('button');
            row.type = 'button';
            row.className = 'oq-capture-toolbar__menu-item';
            row.dataset.workflow = item.id;
            row.setAttribute('role', 'menuitem');
            // Named by the label, described by the sentence (team review's
            // WorkflowMenu rule): a screen reader hears "Auto Review", not 12 words.
            const descId = `${workflowsMenuId}-desc-${item.id}`;
            row.setAttribute('aria-label', item.label);
            row.setAttribute('aria-describedby', descId);
            const text = doc.createElement('span');
            text.className = 'oq-capture-toolbar__menu-item-text';
            const label = doc.createElement('span');
            label.className = 'oq-capture-toolbar__menu-item-label';
            label.textContent = item.label;
            const desc = doc.createElement('span');
            desc.className = 'oq-capture-toolbar__menu-item-desc';
            desc.id = descId;
            desc.textContent = item.description;
            text.append(label, desc);
            const glyph = WORKFLOW_GLYPH_ICONS[item.id];
            row.append(...(glyph ? [createIcon(doc, glyph, `workflow-${item.id}`)] : []), text);
            if (item.locked) {
                row.classList.add('oq-capture-toolbar__menu-item--locked');
                row.setAttribute('aria-haspopup', 'dialog');
                row.setAttribute('aria-label', `${item.label}, ${(options.locks?.label ?? 'Locked').toLowerCase()}`);
                row.append(createIcon(doc, SquareLock02Icon, 'lock'));
            }
            row.addEventListener('click', () => {
                closeMenu(false, 'select');
                if (item.locked)
                    options.locks?.onLocked('workflow', workflowsBtn, item.id);
                else
                    options.workflows?.onSelect(item.id);
            });
            sheet.append(row);
        }
        addMenuKeys(sheet);
        return sheet;
    }
    /** Rows, counts and the all-row follow the options; called at open and on every update while open. */
    function syncMenu() {
        if (menu && openKind === 'create' && options.inspector) {
            updateToggle(menu.querySelector('.oq-toggle'), { checked: options.inspector.enabled });
        }
        const layers = layersOf();
        if (!menu || openKind !== 'highlights' || !layers || !options.highlights)
            return;
        for (const id of LAYER_IDS) {
            const row = menu.querySelector(`[data-layer="${id}"]`);
            updateToggle(row.querySelector('.oq-toggle'), { checked: layers[id].visible });
            row.querySelector('.oq-capture-toolbar__menu-count').textContent = String(layers[id].count);
        }
        const all = menu.querySelector('.oq-capture-toolbar__menu-all');
        const hide = options.highlights.visible;
        const kbd = doc.createElement('kbd');
        kbd.className = 'oq-canvas__toolbar-kbd';
        kbd.textContent = 'V';
        all.replaceChildren(createIcon(doc, hide ? ViewOffIcon : EyeIcon, 'all'), doc.createTextNode(`${hide ? CAPTURE_MODE_COPY.hideAll : CAPTURE_MODE_COPY.showAll} `), kbd);
    }
    /** Menus fit the visible clipping area, independently of the bar's drag bounds.
     * A guest toolbar may be docked in a short row that does not itself clip. */
    function menuBounds() {
        const viewport = win.visualViewport;
        const area = { left: viewport?.offsetLeft ?? 0, top: viewport?.offsetTop ?? 0,
            right: (viewport?.offsetLeft ?? 0) + (viewport?.width ?? win.innerWidth),
            bottom: (viewport?.offsetTop ?? 0) + (viewport?.height ?? win.innerHeight) };
        const parent = (node) => node.parentElement ?? node.getRootNode().host ?? null;
        for (let node = parent(toolbar); node; node = parent(node)) {
            const css = win.getComputedStyle(node), rect = node.getBoundingClientRect();
            if (/^(hidden|clip|auto|scroll)$/.test(css.overflowX || css.overflow)) {
                area.left = Math.max(area.left, rect.left + node.clientLeft);
                area.right = Math.min(area.right, rect.left + node.clientLeft + node.clientWidth);
            }
            if (/^(hidden|clip|auto|scroll)$/.test(css.overflowY || css.overflow)) {
                area.top = Math.max(area.top, rect.top + node.clientTop);
                area.bottom = Math.min(area.bottom, rect.top + node.clientTop + node.clientHeight);
            }
            // A promoted extension host escapes clipping outside the browser's top
            // layer, while its own descendants may still constrain the menu.
            if ((node.hasAttribute('popover') && node.matches(':popover-open'))
                || (node.tagName === 'DIALOG' && node.matches(':modal')))
                break;
        }
        return area;
    }
    /** Prefer above; flip when the lower side has room, and scroll when neither fits. */
    function placeMenu() {
        const anchorEl = anchorOf(openKind);
        if (!menu || !anchorEl)
            return;
        const area = menuBounds();
        const bar = toolbar.getBoundingClientRect();
        const anchor = anchorEl.getBoundingClientRect();
        const width = menu.offsetWidth || 236;
        const left = Math.max(area.left + edgeInset, Math.min(anchor.left + anchor.width / 2 - width / 2, area.right - width - edgeInset));
        menu.style.left = `${Math.round(left - bar.left)}px`;
        const above = Math.max(0, bar.top - area.top - edgeInset * 2);
        const below = Math.max(0, area.bottom - bar.bottom - edgeInset * 2);
        const height = menu.scrollHeight + menu.offsetHeight - menu.clientHeight;
        const opensAbove = height <= above || above >= below;
        menu.dataset.placement = opensAbove ? 'above' : 'below';
        menu.style.top = opensAbove ? '' : `calc(100% + ${DESIGN_RECIPES.spacing.related})`;
        menu.style.bottom = opensAbove ? '' : 'auto';
        menu.style.maxHeight = `${opensAbove ? above : below}px`;
        menu.style.overflowY = 'auto';
        menu.style.transformOrigin = opensAbove ? 'bottom center' : 'top center';
    }
    let stopOutside;
    function onOutsideMousedown(event) {
        const path = event.composedPath();
        const anchor = anchorOf(openKind);
        if (menu && (path.includes(menu) || (anchor && path.includes(anchor))))
            return;
        closeMenu(false, 'outside');
    }
    function openMenu(kind, last = false) {
        const anchor = anchorOf(kind);
        if (!anchor || anchor.disabled || options.record || options.hidden || collapsed || (kind === 'create' ? !options.inspector : kind === 'highlights' ? !layersOf() : !options.workflows))
            return;
        if (menu && openKind === kind) {
            const controls = menu.querySelectorAll('button');
            controls[last ? controls.length - 1 : 0]?.focus();
            return;
        }
        // Never two sheets: opening one closes the other.
        closeMenu(false, 'replaced');
        menu = kind === 'create' ? buildInspectorMenu() : kind === 'highlights' ? buildHighlightsMenu() : buildWorkflowsMenu();
        openKind = kind;
        toolbar.append(menu);
        syncMenu();
        placeMenu();
        anchor.setAttribute('aria-expanded', 'true');
        const controls = menu.querySelectorAll('button');
        controls[last ? controls.length - 1 : 0]?.focus();
        // Armed at once: the opening click's mousedown has already passed.
        stopOutside = listenInOwnerDocument(toolbar, 'mousedown', onOutsideMousedown, true);
        if (kind === 'create')
            track('inspector_menu_opened');
        else
            (kind === 'highlights' ? layersOf()?.onMenuOpen : options.workflows?.onMenuOpen)?.();
        options.onMenuOpenChange?.(true);
    }
    function closeMenu(returnFocus, reason) {
        if (!menu)
            return;
        const anchor = anchorOf(openKind);
        track('capture_menu_closed', { menu: openKind, reason });
        stopOutside?.();
        stopOutside = undefined;
        menu.remove();
        menu = null;
        openKind = null;
        anchor?.setAttribute('aria-expanded', 'false');
        if (returnFocus)
            anchor?.focus();
        options.onMenuOpenChange?.(false);
    }
    for (const kind of ['create', 'highlights', 'workflows']) {
        anchorOf(kind)?.addEventListener('keydown', event => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
                return;
            event.preventDefault();
            event.stopPropagation();
            openMenu(kind, event.key === 'ArrowUp');
        });
    }
    const divider = () => Object.assign(doc.createElement('span'), { className: 'oq-capture-toolbar__divider' });
    /** Padlock on (or off) a control: the badge, the name and tooltip suffix, and (for a tool) the dialog it opens. */
    function applyLock(el, locked, name, tooltip, opensDialog) {
        const label = options.locks?.label ?? '';
        let badge = el.querySelector(':scope > .oq-capture-toolbar__lock');
        if (locked && !badge) {
            badge = Object.assign(doc.createElement('span'), { className: 'oq-capture-toolbar__lock' });
            badge.setAttribute('aria-hidden', 'true');
            badge.append(createIcon(doc, SquareLock02Icon, 'lock'));
            el.append(badge);
        }
        else if (!locked)
            badge?.remove();
        el.classList.toggle('oq-capture-toolbar__locked', locked);
        el.setAttribute('aria-label', locked ? `${name}, ${label.toLowerCase()}` : name);
        el.dataset.tooltip = locked ? `${tooltip} · ${label}` : tooltip;
        if (opensDialog) {
            if (locked)
                el.setAttribute('aria-haspopup', 'dialog');
            else
                el.removeAttribute('aria-haspopup');
        }
    }
    const dividers = initial.tools ? [divider(), divider()] : [];
    // Collapse is the user's choice and survives Done: the browse-mode bar is
    // then grip + chevron, and the chevron is the way back (prototype v15).
    let collapsed = false;
    const collapse = initial.tools ? doc.createElement('button') : null;
    if (collapse) {
        collapse.type = 'button';
        collapse.className = 'oq-capture-toolbar__collapse';
        collapse.addEventListener('click', () => { collapsed = !collapsed; track('capture_toolbar_collapsed', { collapsed }); if (collapsed) {
            disableSpacing();
            closeMenu(false, 'collapsed');
        } update(options); });
    }
    toolbar.append(grip, ...tools, ...(recordTool ? [recordTool] : []), ...(workflowsBtn ? [workflowsBtn] : []), ...dividers.slice(0, 1), spacingHost, ...(highlightsGroup ? [highlightsGroup] : highlightsBtn ? [highlightsBtn] : []), ...dividers.slice(1), hint, pause, done, ...(collapse ? [collapse] : []));
    container.append(style, toolbar);
    const tooltips = mountToolbarTooltips(toolbar, undefined, 'oq-capture-tooltip--app14');
    let position = null;
    let drag = null;
    function bounds() {
        if (options.position === 'absolute') {
            const parent = toolbar.offsetParent;
            const rect = (parent || container).getBoundingClientRect();
            return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
        }
        return { left: 0, top: 0, width: win.innerWidth, height: win.innerHeight };
    }
    function move(x, y) {
        const area = bounds();
        const rect = toolbar.getBoundingClientRect();
        if (!rect.width || !rect.height)
            return false;
        const previous = position;
        position = {
            x: Math.max(edgeInset, Math.min(x, area.width - rect.width - edgeInset)),
            y: Math.max(edgeInset, Math.min(y, area.height - rect.height - edgeInset)),
        };
        Object.assign(toolbar.style, { left: `${position.x}px`, top: `${position.y}px`, bottom: 'auto', transform: 'none' });
        placeMenu();
        const changed = previous ? previous.x !== position.x || previous.y !== position.y : rect.left - area.left !== position.x || rect.top - area.top !== position.y;
        if (changed)
            getUiEventTarget(doc).dispatchEvent(new CustomEvent('oq:capture-toolbar-moved'));
        return changed;
    }
    function reset() {
        position = null;
        Object.assign(toolbar.style, { left: options.center || '', top: '', bottom: '', transform: '' });
        reclamp();
        getUiEventTarget(doc).dispatchEvent(new CustomEvent('oq:capture-toolbar-moved'));
    }
    grip.addEventListener('pointerdown', event => {
        if (event.button !== 0 || event.isPrimary === false)
            return;
        event.preventDefault();
        event.stopPropagation();
        grip.focus({ preventScroll: true });
        const rect = toolbar.getBoundingClientRect();
        drag = { id: event.pointerId, dx: event.clientX - rect.left, dy: event.clientY - rect.top, moved: false };
        grip.setPointerCapture(event.pointerId);
    });
    grip.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId)
            return;
        event.preventDefault();
        event.stopPropagation();
        const area = bounds();
        drag.moved = move(event.clientX - drag.dx - area.left, event.clientY - drag.dy - area.top) || drag.moved;
    });
    const endDrag = (event) => {
        if (!drag || drag.id !== event.pointerId)
            return;
        if (drag.moved)
            track('capture_toolbar_moved', { via: 'drag' });
        drag = null;
        if (grip.hasPointerCapture(event.pointerId))
            grip.releasePointerCapture(event.pointerId);
    };
    grip.addEventListener('pointerup', endDrag);
    grip.addEventListener('pointercancel', endDrag);
    grip.addEventListener('lostpointercapture', endDrag);
    grip.addEventListener('keydown', event => {
        const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const direction = directions[event.key];
        if (!direction && event.key !== 'Home')
            return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Home') {
            reset();
            track('capture_toolbar_reset', { via: 'keyboard' });
            return;
        }
        const area = bounds();
        const rect = toolbar.getBoundingClientRect();
        const step = event.shiftKey ? 32 : 8;
        if (move((position?.x ?? rect.left - area.left) + direction[0] * step, (position?.y ?? rect.top - area.top) + direction[1] * step))
            track('capture_toolbar_moved', { via: 'keyboard' });
    });
    // Browse mode has no capture canvas to fence these presses and activations.
    // Releases still reach document listeners for drags begun outside the bar.
    for (const type of ['pointerdown', 'mousedown', 'click', 'dblclick'])
        toolbar.addEventListener(type, event => event.stopPropagation());
    pause.addEventListener('click', () => options.onTogglePause());
    done.addEventListener('click', () => { disableSpacing(); options.onDone(); });
    const reclamp = () => {
        if (!position) {
            // The preferred center can reserve a sidebar wider than the available
            // page. Keep Home/initial placement visible too, without turning it into
            // a dragged position: a later resize must restore the preferred center.
            toolbar.style.left = options.center || '';
            const area = bounds();
            const rect = toolbar.getBoundingClientRect();
            if (rect.width && (rect.left < area.left + edgeInset || rect.right > area.left + area.width - edgeInset)) {
                const left = Math.max(edgeInset, Math.min(rect.left - area.left, area.width - rect.width - edgeInset));
                toolbar.style.left = `${left + rect.width / 2}px`;
            }
        }
        const height = toolbar.getBoundingClientRect().height;
        if (height)
            layoutOwner?.style.setProperty('--oq-capture-toolbar-height', `${height}px`);
        if (position)
            move(position.x, position.y);
        placeMenu();
    };
    win.addEventListener('resize', reclamp);
    const visualViewport = win.visualViewport;
    visualViewport?.addEventListener('resize', reclamp);
    visualViewport?.addEventListener('scroll', reclamp);
    // Capturing ancestor scrolls keeps a docked menu inside its visible area.
    // Its own scroll only moves rows, so it must not cause another placement.
    const onMenuAncestorScroll = (event) => { if (menu && event.target !== menu && !(event.target instanceof win.Node && menu.contains(event.target)))
        placeMenu(); };
    doc.addEventListener('scroll', onMenuAncestorScroll, true);
    const observer = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(reclamp) : null;
    observer?.observe(reviewLayer || container);
    observer?.observe(toolbar);
    // Morph, not snap (v15): when a tools bar leaves capture mode, Pause and
    // Done exit on the M3 accelerate curve and the bar then contracts to its
    // browse width on ease-in-out. Measured on the prototype 2026-10-15, the
    // exponential ease-out did 43% of the travel in its first 14 ms and still
    // read as a snap; ease-in-out peaks at 28 px per frame over this 195 px
    // change. Under reduced motion, and in jsdom (no Web Animations), both
    // stages are immediate.
    const reducedMotion = () => typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canAnimate = () => !reducedMotion() && [pause, done, toolbar].every(el => typeof el.animate === 'function');
    let exits = [];
    let contraction = null;
    /** Drop an in-flight morph, so Pause and Done come back at full opacity on a re-arm. */
    const settleMorph = () => {
        if (!exits.length && !contraction)
            return;
        for (const animation of [...exits, ...(contraction ? [contraction] : [])])
            animation.cancel();
        exits = [];
        contraction = null;
        toolbar.style.width = '';
        toolbar.style.overflow = '';
    };
    /** Resolves true once the bar has settled in browse mode, false when a re-arm cancelled the morph. */
    function morph(before) {
        // Freeze the armed width while the buttons fade, so the hint and divider
        // leaving synchronously cannot shift the bar before the contraction.
        toolbar.style.width = `${before}px`;
        toolbar.style.overflow = 'hidden';
        const easing = win.getComputedStyle(toolbar).getPropertyValue('--oq-ease-in-m3').trim() || EXIT_EASING_FALLBACK;
        const mine = [pause, done].map(button => button.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.9)' }], { duration: 120, easing, fill: 'forwards' }));
        exits = mine;
        return Promise.all(mine.map(animation => animation.finished)).then(() => {
            if (exits !== mine)
                return false; // a re-arm settled this morph already
            for (const button of [pause, done])
                button.style.display = 'none';
            for (const animation of mine)
                animation.cancel();
            exits = [];
            toolbar.style.width = '';
            const after = toolbar.getBoundingClientRect().width;
            const shrink = toolbar.animate([{ width: `${before}px` }, { width: `${after}px` }], { duration: 200, easing: 'ease-in-out' });
            contraction = shrink;
            return shrink.finished.then(() => {
                if (contraction !== shrink)
                    return false; // re-armed mid-contraction
                contraction = null;
                toolbar.style.overflow = '';
                return true;
            });
        }).catch(() => false); // only a cancel() rejects `finished`, and settleMorph already restored the bar
    }
    function update(next) {
        const leavingCapture = !!next.tools && options.mode !== 'idle' && next.mode === 'idle';
        const armedWidth = leavingCapture && canAnimate() ? toolbar.getBoundingClientRect().width : null;
        if (options.featureGuides !== next.featureGuides) {
            spacing?.destroy();
            spacing = undefined;
            recordDot?.destroy();
            recordDot = undefined;
            inspectorDot?.destroy();
            inspectorDot = undefined;
        }
        options = next;
        if (createGroup && createCaret) {
            if (options.inspector) {
                if (!createGroup.isConnected) {
                    tools[0].replaceWith(createGroup);
                    createGroup.append(tools[0], createCaret);
                }
                createCaret.disabled = !!options.record;
                if (options.featureGuides && !inspectorDot && !options.record)
                    inspectorDot = mountFeatureDot(createCaret, 'inspector', options.featureGuides, () => openMenu('create'));
            }
            else {
                if (openKind === 'create')
                    closeMenu(false, 'hidden');
                inspectorDot?.destroy();
                inspectorDot = undefined;
                if (createGroup.isConnected)
                    createGroup.replaceWith(tools[0]);
            }
            if (options.record) {
                inspectorDot?.destroy();
                inspectorDot = undefined;
            }
        }
        syncMenu();
        const idle = options.mode === 'idle';
        if (!idle)
            settleMorph();
        if (options.showSpacing !== false && !spacing)
            spacing = mountSpacingInspectorButton(spacingHost, 'capture', options.featureGuides, enabled => { if (!options.hidden)
                track('spacing_toggled', { enabled }); });
        else if (options.showSpacing === false && spacing) {
            disableSpacing();
            spacing.destroy();
            spacing = undefined;
        }
        toolbar.style.position = options.position || 'fixed';
        toolbar.classList.toggle('oq-capture-toolbar--tools', !!options.tools);
        toolbar.classList.toggle('oq-capture-toolbar--collapsed', collapsed);
        toolbar.setAttribute('aria-label', idle ? 'Page tools' : 'Capture controls');
        const state = options.isLoading ? 'loading' : options.isPaused ? 'paused' : options.mode;
        // With the tools in the bar the pressed camera already says "screenshot
        // mode", so the hint carries only what the tools cannot: loading, paused,
        // and the drag instruction. Without tools it behaves as it always did.
        hint.style.display = idle || (options.tools && !['loading', 'paused', 'screenshot'].includes(state)) ? 'none' : '';
        if (armedWidth === null)
            for (const control of [pause, done])
                control.style.display = idle ? 'none' : '';
        if (options.hidden || idle)
            tooltips.hide();
        // A hidden or collapsed bar keeps no sheet open, whichever menu it is.
        if (options.hidden || collapsed || options.record)
            closeMenu(false, options.hidden || options.record ? 'hidden' : 'collapsed');
        toolbar.style.display = options.hidden ? 'none' : '';
        toolbar.setAttribute('aria-hidden', String(!!options.hidden));
        if (!position)
            toolbar.style.left = options.center || '';
        // Only the hint changes; the focused Pause/Resume button and grip survive.
        if (hint.dataset.state !== state) {
            hint.dataset.state = state;
            hint.replaceChildren();
            if (state === 'loading')
                hint.textContent = 'Loading saved issues…';
            else if (state === 'paused')
                hint.textContent = 'Capture paused';
            else {
                const key = doc.createElement('kbd');
                key.className = 'oq-canvas__toolbar-kbd';
                key.textContent = state === 'screenshot' ? 'Esc' : 'S';
                if (state === 'screenshot')
                    hint.append(options.tools ? 'Drag to capture · ' : 'Screenshot mode · Drag to capture · ', key, ' to cancel');
                else if (!options.tools) {
                    key.title = 'Screenshot';
                    key.setAttribute('aria-label', 'Screenshot shortcut: S');
                    hint.append(key);
                }
            }
        }
        // The Record states (docs/designs/recordings-on-issues.md): the shipped hint, Pause and Done step aside for the
        // phase's hint and primary button, which join the DOM only while a state is set, so the bar is byte-identical
        // to before when nothing records. Select and Screenshot stay visible but off while a take runs.
        const rec = options.record;
        if (options.tools?.record)
            ensureRecordTool();
        if (recordDot && (!options.tools?.record || rec)) {
            recordDot.destroy();
            recordDot = undefined;
        }
        if (recordTool && options.tools?.record && !rec && options.featureGuides && !recordDot && !options.locks?.record) {
            recordDot = mountFeatureDot(recordTool, 'recorder', options.featureGuides);
        }
        if (recordTool) {
            recordTool.style.display = options.tools?.record ? '' : 'none';
            recordTool.setAttribute('aria-label', options.tools?.record?.label ?? '');
            recordTool.dataset.tooltip = options.tools?.record?.label ?? '';
            recordTool.setAttribute('aria-pressed', String(!!rec));
        }
        if (rec) {
            for (const control of [hint, pause, done])
                control.style.display = 'none';
            recordHint.replaceChildren();
            if (rec.phase === 'area') {
                const key = doc.createElement('kbd');
                key.className = 'oq-canvas__toolbar-kbd';
                key.textContent = 'Esc';
                recordHint.append(key, ` ${rec.hint}`);
            }
            else
                recordHint.textContent = rec.hint;
            if (!recordHint.isConnected)
                pause.before(recordHint);
            if (rec.primary) {
                recordPrimary.replaceChildren(createIcon(doc, rec.phase === 'recording' ? StopIcon : Video01Icon, 'record'), Object.assign(doc.createElement('span'), { textContent: rec.primary.label }));
                recordPrimary.setAttribute('aria-label', rec.primary.label);
                if (!recordPrimary.isConnected)
                    done.before(recordPrimary);
            }
            else
                recordPrimary.remove();
        }
        else {
            recordHint.remove();
            recordPrimary.remove();
        }
        if (options.tools) {
            const labels = { select: options.tools.selectLabel ?? '', screenshot: options.tools.screenshotLabel ?? '' };
            tools.forEach((button, index) => {
                const tool = TOOL_KINDS[index];
                button.style.display = tool === 'screenshot' && options.tools?.screenshot === false ? 'none' : '';
                button.setAttribute('aria-label', labels[tool]);
                button.dataset.tooltip = labels[tool];
                button.setAttribute('aria-pressed', String(!idle && !options.isPaused && !rec && options.mode === tool));
                button.disabled = !!rec;
            });
            // A guest bar has no Spacing (and no eye) and shows ONE divider, between
            // the tools and Pause; browse mode shows none after the middle group
            // because nothing follows it.
            const noMiddle = options.showSpacing === false && !highlightsBtn;
            dividers[0].style.display = idle && noMiddle ? 'none' : '';
            dividers[1].style.display = idle || noMiddle ? 'none' : '';
        }
        // Locks go on after every label above has been written, so the padlock's suffix is never overwritten.
        const locks = options.locks;
        const spacingBtn = spacingHost.querySelector('.oq-capture-toolbar__spacing');
        if (spacingBtn) {
            const pressed = spacingBtn.getAttribute('aria-pressed') === 'true';
            applyLock(spacingBtn, !!locks?.spacing, 'Spacing', pressed ? 'Hide padding and spacing · Esc' : 'Show padding and spacing', true);
        }
        if (recordTool && options.tools?.record) {
            const name = options.tools.record.label.split(' · ')[0];
            if (locks?.record)
                applyLock(recordTool, true, name, name, true);
            else if (recordTool.classList.contains('oq-capture-toolbar__locked'))
                applyLock(recordTool, false, options.tools.record.label, options.tools.record.label, true);
        }
        if (workflowsBtn) {
            const items = options.workflows?.items ?? [];
            applyLock(workflowsBtn, !!items.length && items.every(item => item.locked), CAPTURE_MODE_COPY.workflows, CAPTURE_MODE_COPY.workflows, false);
        }
        if (highlightsBtn && options.highlights) {
            const { visible, label, layers } = options.highlights;
            if (highlightsBtn.dataset.visible !== String(visible)) {
                highlightsBtn.dataset.visible = String(visible);
                highlightsBtn.replaceChildren(createIcon(doc, visible ? EyeIcon : ViewOffIcon, 'highlights'));
            }
            highlightsBtn.setAttribute('aria-label', label);
            // Some layers on, some off: the pressed eye with the brand dot.
            const mixed = !!layers && visible && !(layers.pins.visible && layers.boxes.visible);
            highlightsBtn.setAttribute('aria-pressed', mixed ? 'mixed' : String(visible));
            highlightsBtn.classList.toggle('oq-capture-toolbar__highlights--mixed', mixed);
            highlightsBtn.dataset.tooltip = label;
            syncMenu();
        }
        const pauseLabel = options.isPaused ? CAPTURE_MODE_COPY.resume : CAPTURE_MODE_COPY.pause;
        if (pause.getAttribute('aria-label') !== pauseLabel)
            pause.replaceChildren(createIcon(doc, options.isPaused ? PlayIcon : PauseIcon, options.isPaused ? 'play' : 'pause'));
        pause.setAttribute('aria-label', pauseLabel);
        pause.setAttribute('aria-pressed', String(!!options.isPaused));
        pause.dataset.tooltip = pauseLabel;
        pause.className = `oq-canvas__pause-btn${options.isPaused ? ' oq-canvas__pause-btn--active' : ''}`;
        doneLabel.textContent = options.isSaving ? 'Saving...' : CAPTURE_MODE_COPY.done;
        done.dataset.tooltip = options.isSaving ? 'Saving...' : CAPTURE_MODE_COPY.done;
        done.setAttribute('aria-label', options.isSaving ? 'Saving...' : CAPTURE_MODE_COPY.done);
        done.setAttribute('aria-busy', String(!!options.isSaving));
        done.disabled = !!options.isSaving;
        if ((options.count ?? 0) > 0) {
            countPill.textContent = String(options.count);
            if (countPill.parentElement !== done)
                done.append(countPill);
        }
        else
            countPill.remove();
        if (collapse) {
            const label = collapsed ? CAPTURE_MODE_COPY.showTools : CAPTURE_MODE_COPY.hideTools;
            if (collapse.getAttribute('aria-label') !== label)
                collapse.replaceChildren(createIcon(doc, collapsed ? ArrowRight01Icon : ArrowLeft01Icon, 'collapse'));
            collapse.setAttribute('aria-label', label);
            collapse.setAttribute('aria-expanded', String(!collapsed));
            collapse.dataset.tooltip = label;
        }
        reclamp();
        if (!leavingCapture)
            return Promise.resolve();
        // The surface only sets its mode to idle; the renderer announces the end
        // of capture once the browse morph has settled (or at once without motion).
        // A re-arm mid-morph cancels it and announces nothing: capture is live again.
        const announce = () => { getUiEventTarget(doc).dispatchEvent(new CustomEvent('oq:capture-done')); };
        if (armedWidth === null) {
            announce();
            return Promise.resolve();
        }
        return morph(armedWidth).then(ended => { if (ended)
            announce(); });
    }
    update(initial);
    return { update, destroy() { closeMenu(false, 'unmount'); inspectorDot?.destroy(); tooltips.destroy(); if (!options.preserveSpacingOnDestroy)
            disableSpacing(); spacing?.destroy(); recordDot?.destroy(); settleMorph(); observer?.disconnect(); win.removeEventListener('resize', reclamp); visualViewport?.removeEventListener('resize', reclamp); visualViewport?.removeEventListener('scroll', reclamp); doc.removeEventListener('scroll', onMenuAncestorScroll, true); style.remove(); toolbar.remove(); } };
}
//# sourceMappingURL=captureToolbarDom.js.map